import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./Chat.css";
import Button from "react-bootstrap/Button";
import { SocketContext } from "../../socket/SocketContext";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import speakericon from "../../images/MIC.svg";
import { format, isToday, isYesterday } from "date-fns";
import { uploadMessageImage } from "../services/notificationservice";
import { apiUrl, PORT } from "../../environment";
import { AiFillDelete } from "react-icons/ai";
import { toast } from "react-toastify";

const SpeechRecognition =
  window.SpeechRecognition ||
  window.webkitSpeechRecognition ||
  window.mozSpeechRecognition ||
  window.msSpeech;
var mic;
if (!SpeechRecognition) {
  console.error("Speech Recognition API is not supported in this browser.");
} else {
  mic = new SpeechRecognition();
  mic.continuous = true;
  mic.interimResults = true;
  mic.lang = "en-US";
}

function ChatComponent() {
  const token = useSelector((state) => state?.login?.data?.accessToken);

  const { data } = useSelector((state) => state.login);
  const [messagechat, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [userStatus, setUserStatus] = useState(false);
  const location = useLocation();
  const { patient } = location.state || {};
  const [pageNumber, setPageNumber] = useState(1);
  const socket = useContext(SocketContext);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const patientId = patient?._id;

  const [totalPages, setTotalPages] = useState(1);

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  // Ref for scrolling to bottom
  const chatContainerRef = useRef(null);

  const [isListening, setIsListening] = useState(false);

  // Inactivity timeout timer
  const inactivityTimeout = useRef(null);

  useEffect(() => {
    if (!SpeechRecognition) {
      return;
    }
    handleListen();
  }, [isListening]);

  const handleListen = () => {
    if (isListening) {
      mic.start();
      mic.onend = () => {
        mic.start();
      };
    } else {
      mic.stop();
      mic.onend = () => {
      };
    }
    mic.onstart = () => {
    };

    mic.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0])
        .map((result) => result.transcript)
        .join("");
      setMessage(transcript);

      // Reset inactivity timer when the user speaks
      resetInactivityTimer();
    };

    mic.onerror = (event) => {
      setMessage("");
    };
  };

  const resetInactivityTimer = () => {
    // Clear any existing timer
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }

    // Set a new timer to stop listening after a period of inactivity
    inactivityTimeout.current = setTimeout(() => {
      if (isListening) {
        setIsListening(false); // Automatically stop listening
      }
    }, 3000); // after 3 sec mic stop
  };

  const getDateLabel = (date) => {
    const today = moment().startOf("day");
    const chatDate = moment(date).startOf("day");

    if (chatDate.isSame(today, "day")) {
      return "Today";
    } else if (chatDate.isSame(today.subtract(1, "day"), "day")) {
      return "Yesterday";
    } else {
      return moment(date).format("MMMM D, YYYY");
    }
  };

  const sendMessage = () => {
    // Prevent sending empty messages
    if (!messagechat.trim()) return;

    const messageData = {
      senderType: "User",
      from_id: data._id,
      patientId: patientId,
      userId: data._id,
      providerId: data._id,
      message: messagechat,
      type: "chat",
      sentAt: new Date(),
    };

    socket?.emit("create-chat", messageData);
    setMessage("");
  };

  const handleChanegMessageValue = (e) => {
    setMessage(e.target.value);
  };

  const handleChatReceived = useCallback(
    (message) => {

      if (data._id === message?.userId && patientId === message?.patientId) {
        setChatHistory((prevChatHistory) => {
          const dateLabel = getDateLabel(message.createdAt); // Get the date label for the message

          // Initialize the updated chat history object
          const updatedHistory = { ...prevChatHistory };

          if (prevChatHistory[dateLabel]) {
            // If the dateLabel exists append the message to the existing array
            updatedHistory[dateLabel] = [
              ...prevChatHistory[dateLabel],
              message,
            ];
          } else {
            // If the dateLabel doesn't exist, add the message to the top of the chatHistory
            updatedHistory[dateLabel] = [message];
          }
          // reordering the object is necessary by constructing a new object with the new label at the top
          const orderedHistory = {
            [dateLabel]: updatedHistory[dateLabel],
            ...updatedHistory,
          };

          return orderedHistory;
        });
      }

      // scroll to the bottom when a new message is received
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    },
    [data._id, patientId]
  );

  useEffect(() => {
    socket?.emit("userConnected", {
      providerId: data._id,
      patientId: patientId,
      role: "DOCTOR",
    });

    socket?.on("get-chats-received", handleChatReceived);

    const messageData = { patientId, providerId: data._id, page: page };
    socket?.emit("get-chat-list", messageData);

    socket?.on("show-chats-list", (messagelist) => {


      setPage(messagelist.currentPage);

      setTotalPages(messagelist.totalPages);

      const groupedMessages = groupMessagesByDate(
        Array.isArray(messagelist?.list) ? messagelist?.list : []
      );

      setChatHistory(groupedMessages);
    });
    setIsLoading(false);

    socket?.on("patient-status", (patientIds, status) => {
      const isActive = patientIds == patientId && status == "active";
      setUserStatus(isActive);
    });

    socket?.on("error", (err) => {
      alert("you are not authorized to connect");
    });

    return () => {
      socket?.off("get-chats-received", handleChatReceived);
      socket?.off("show-chats-list");
      socket?.emit("inactive", data._id, data._id);
      socket?.emit("userDisconnected", { userId: data._id });
      socket?.off("patient-status");
      socket?.off("provider-status");
    };
  }, [socket, patientId]);

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    if (chatContainerRef.current.scrollTop === 0 && page <= totalPages) {

      setPage((prevPage) => {
        const newPage = prevPage + 1;
        socket?.emit("get-chat-list", {
          patientId,
          providerId: data._id,
          page: newPage,
        });
        return newPage;
      });

      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = 10;
      }
    }
  };


  //*******************************new logic from here ********************************** */

  const grouped = {};

  const groupMessagesByDate = (messages) => {
    messages.forEach((message) => {
      const createdAt = new Date(message?.createdAt);

      let label;
      if (isToday(createdAt)) {
        label = "Today";
      } else if (isYesterday(createdAt)) {
        label = "Yesterday";
      } else {
        label = format(createdAt, "yyyy-MM-dd"); // Format as specific date
      }

      // Group messages under the label
      if (!grouped[label]) {
        grouped[label] = [];
      }
      grouped[label].push(message);
    });

    // Sort messages within each group by createdAt (oldest to newest)
    for (const label in grouped) {
      grouped[label].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    }


    // Return grouped messages sorted by label (newest to oldest group)
    return Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a)) // Sort labels descending
      .reduce((sorted, label) => {
        sorted[label] = grouped[label];
        return sorted;
      }, {});
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files); // Get all selected files

    // Check if all files are images
    const nonImageFiles = selectedFiles.filter(
      (file) => !file.type.startsWith("image/")
    );

    if (nonImageFiles.length > 0) {
      // Show alert if non-image files are selected
      toast.error("Please select only image files (e.g., JPG, PNG, etc.).");
      return; // Stop further processing
    }

    // Proceed with valid image files
    setFiles(selectedFiles);

    // Generate previews for selected files
    const previewUrls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviews(previewUrls);
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();

    // Upload file to the server
    const formData = new FormData();
    // Append each file individually
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await uploadMessageImage(formData, token);

      if (response.code === 200) {
        setMessage("");
        setFiles([]);
        setPreviews([]);

        const transformedMedia = response.data?.length
          ? response.data.map((file) => {
            const mediaType = file.mimetype.split("/")[0];
            const mediaPath = `${mediaType}/${file.filename}`;

            return {
              mediaType: mediaType,
              mediaUrl: mediaPath,
              mediaName: file.originalname,
            };
          })
          : [];

        const messageData = {
          senderType: "User",
          from_id: data._id,
          patientId: patientId,
          userId: data._id,
          providerId: data._id,
          message: messagechat,
          media: transformedMedia,
          sentAt: new Date(),
        };

        socket?.emit("create-chat", messageData); // Emit file info
      }
    } catch (err) {
      console.error("File upload failed:", err);
    }
  };

  // Handle file deletion
  const handleDeleteFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const colors = useMemo(() => ["#64D2FF"], []);

  // **********************************************************************************************

  const textareaRef = useRef(null);

  const MAX_ROWS = 5; // Maximum number of rows before scrolling starts

  // Adjust the height based on the content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset the height
      const lineHeight = parseInt(
        window.getComputedStyle(textarea).lineHeight,
        10
      );
      const maxHeight = MAX_ROWS * lineHeight;
      const scrollHeight = textarea.scrollHeight;

      if (scrollHeight <= maxHeight) {
        textarea.style.height = `${scrollHeight}px`;
      } else {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = "auto";
      }
    }
  }, [messagechat]); // Runs whenever messageText changes

  return (
    <div className="chat-block-area d-flex">
      <div className="main-chatbox" style={{ height: "75vh" }}>
        <div className="people-info d-flex align-items-start justify-content-between">
          <div className="profile-person-block d-flex">
            <div className="profile-pic">
              <div className="person-pic">
                {patient?.photoUrl ? (
                  <img src={patient.photoUrl} alt="patient-img" />
                ) : (
                  <div
                    className="patient-initials"
                    style={{
                      background:
                        colors[Math.floor(Math.random() * colors.length)],
                    }}
                  >
                    {patient?.firstName || patient?.lastName
                      ? `${patient?.firstName?.[0] || ""}${patient?.lastName?.[0] || ""
                        }`.toUpperCase()
                      : (patient?.email?.[0] || "").toUpperCase()}
                  </div>
                )}
              </div>
              {userStatus && <div className="status-icon"></div>}
            </div>
            <div className="person-info">
              {patient?.firstName || patient?.lastName
                ? `${patient?.firstName || "-"} ${patient?.lastName || ""}`
                : patient?.email || "-"}
              <span>
                Chat with{" "}
                {patient?.firstName || patient?.lastName
                  ? `${patient?.firstName || "-"} ${patient?.lastName || ""}`
                  : patient?.email || "-"}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center"></div>
        </div>
        <div
          className="chat-messages"
          ref={chatContainerRef}
          onScroll={handleScroll}
        >
          {Object.keys(chatHistory)
            ?.reverse()
            .map((dateLabel) => (
              <div key={dateLabel} className="chat-group">
                <div className="date-label">{dateLabel}</div>
                {chatHistory[dateLabel]?.map((chat, index) =>
                  chat.senderType === "User" ? (
                    <div key={index} className="incoming-texts mb-2">
                      {chat?.media?.length > 0 ? (
                        <div className="my-textmessages outgoing-texts-span text-with-image mb-1">
                          <span>
                            {chat.media.map((media) => (
                              <div>
                                <a
                                  href={`${apiUrl}${PORT}/patient-service/get-files/${media.mediaUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    className="mb-2 border-sm shadow-sm"
                                    style={{
                                      height: "auto",
                                      width: "280px",
                                      objectFit: "contain",
                                    }}
                                    src={`${apiUrl}${PORT}/patient-service/get-files/${media.mediaUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer "
                                    alt="media"
                                  />
                                </a>
                              </div>
                            ))}
                            {chat.message.length > 0 && (
                              <>
                                {chat.message}
                                <p>
                                  {" "}
                                  {new Date(chat?.createdAt).toLocaleString(
                                    "en-US",
                                    {
                                      hour12: true,
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      // second: '2-digit'
                                    }
                                  )}
                                </p>
                              </>
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="my-textmessages">
                          <span
                            className="outgoing-texts-span"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {chat.message}
                            <p>
                              {" "}
                              {new Date(chat?.createdAt).toLocaleString(
                                "en-US",
                                {
                                  hour12: true,
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  // second: '2-digit'
                                }
                              )}
                            </p>
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div key={index} className="outgoing-texts mb-2">
                      {chat?.media?.length > 0 ? (
                        <div className="my-textmessages delivered-messages text-with-image mb-1">
                          <span>
                            {chat.media.map((media) => (
                              <div>
                                <a
                                  href={`${apiUrl}${PORT}/patient-service/get-files/${media.mediaUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    className="mb-2 border-sm "
                                    style={{
                                      height: "auto",
                                      maxHeight: "280px",
                                      width: "280px",
                                      objectFit: "contain",
                                    }}
                                    src={`${apiUrl}${PORT}/patient-service/get-files/${media.mediaUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer "
                                    alt="media"
                                  />
                                </a>
                              </div>
                            ))}
                            {chat.message.length > 0 && <>{chat.message}</>}

                            <p>
                              {" "}
                              {new Date(chat?.createdAt).toLocaleString(
                                "en-US",
                                {
                                  hour12: true,
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  // second: '2-digit'
                                }
                              )}
                            </p>
                          </span>
                        </div>
                      ) : (
                        <div className="my-textmessages  delivered-messages">
                          <span
                            className="outgoing-texts-span "
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {chat.message}
                            <p>
                              {" "}
                              {new Date(chat?.createdAt).toLocaleString(
                                "en-US",
                                {
                                  hour12: true,
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  // second: '2-digit'
                                }
                              )}
                            </p>
                          </span>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            ))}
        </div>
        <div className="text-box-block d-flex justify-content-between">
          <div className="left-textbox">
            <div className="input-container">
              <div style={{ display: "flex", width: "100%", flex: 1, minWidth: 0 }}>
                <div className="d-flex align-items-center">
                  {" "}
                  <span className="plus-icon">+</span>{" "}
                </div>
                <input
                  type="file"
                  className="file-input"
                  multiple
                  onChange={handleFileChange}
                />
                <textarea
                  ref={textareaRef}
                  rows="1"
                  type="text"
                  placeholder="Type something"
                  className="text-input"
                  value={messagechat}
                  onChange={handleChanegMessageValue}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
              </div>
              <div className="icon-container">
                <button
                  className="speaker-icon"
                  onClick={() => setIsListening((prevState) => !prevState)}
                  style={{ border: "none" }}
                >
                  {isListening ? (
                    <span>🎙️</span>
                  ) : (
                    <img src={speakericon} alt="Speaker Icon" />
                  )}
                </button>
              </div>
              <br />

              {/* File previews */}
              {previews.length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  {files.map((preview, index) => (
                    <div key={index} style={{ marginBottom: "5px" }}>
                      <div
                        style={{
                          backgroundColor: "#DEB887",
                          padding: "5px",
                          borderRadius: "6px",
                        }}
                      >
                        <a
                          href={`${apiUrl}${PORT}/patient-service/get-files/${preview.mediaUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {files[index].name}
                        </a>

                        <AiFillDelete
                          style={{ cursor: "pointer" }}
                          className="text-danger delete-icon-chat cursor"
                          onClick={() => handleDeleteFile(index)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {files && files.length > 0 ? (
            <Button className="filled-btn" onClick={handleFileSubmit}>
              Send
            </Button>
          ) : (
            <Button className="filled-btn" onClick={sendMessage}>
              Send
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatComponent;
